export interface ApiError {
  status: number;
  message: string;
  details?: unknown;
}

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();
const API_BASE_URL = (
  configuredApiBaseUrl || (import.meta.env.DEV ? 'http://localhost:3001' : '')
).replace(/\/+$/, '');

function apiUrl(path: string): string {
  return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

/**
 * Best-effort coercion of an arbitrary error body into a single human string.
 * The conventional shape from this backend is `{ error: string, details?: ... }`,
 * but we also gracefully handle:
 *  - older shapes where `error` is itself a Zod-flatten object,
 *  - shapes where details carry the field errors,
 *  - non-JSON bodies (already a string).
 */
function extractErrorMessage(body: unknown, status: number): string {
  if (typeof body === 'string' && body.trim().length > 0) return body;
  if (body && typeof body === 'object') {
    const obj = body as { error?: unknown; details?: unknown; message?: unknown };
    if (typeof obj.error === 'string' && obj.error.trim().length > 0) return obj.error;
    if (typeof obj.message === 'string' && obj.message.trim().length > 0) return obj.message;

    // Walk the Zod-flatten shape that some routes used to return raw.
    const candidates: Array<{ fieldErrors?: unknown; formErrors?: unknown }> = [];
    if (obj.error && typeof obj.error === 'object') candidates.push(obj.error);
    if (obj.details && typeof obj.details === 'object') candidates.push(obj.details);
    for (const c of candidates) {
      const fieldErrors = c.fieldErrors as Record<string, unknown> | undefined;
      if (fieldErrors && typeof fieldErrors === 'object') {
        for (const [field, msgs] of Object.entries(fieldErrors)) {
          if (Array.isArray(msgs) && typeof msgs[0] === 'string') {
            return `${field}: ${msgs[0]}`;
          }
        }
      }
      const formErrors = c.formErrors;
      if (Array.isArray(formErrors) && typeof formErrors[0] === 'string') {
        return formErrors[0];
      }
    }
  }
  return `Request failed with status ${status}`;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(apiUrl(path), {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => '');
    }
    const err: ApiError = {
      status: res.status,
      message: extractErrorMessage(body, res.status),
      details: body,
    };
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type UserRole = 'superadmin' | 'org_admin' | 'user';

export interface AuthUser {
  id: string;
  email?: string;
  organizationId?: string;
  role: UserRole;
  mustChangePassword?: boolean;
}

export interface ComponentItem {
  id: string;
  organization_id: string;
  owner_user_id: string;
  owner_email: string;
  name: string;
  description: string;
  is_active: boolean;
  latest_version: number | null;
  /** Version currently promoted into the CDN's `current/` mirror (what
   *  pasted snippets serve). May trail `latest_version` when newer
   *  uploads haven't been promoted yet; null when nothing is live. */
  current_version: number | null;
  created_at: string;
  updated_at: string;
}

export interface ComponentVersionFile {
  /** Relative path inside the bundle (e.g. `assets/logo.svg`). */
  path: string;
  /** Full URL the browser/embedder fetches — points at the CDN's immutable
   *  versioned copy. Always safe to use as `<a href>` / `<script src>`. */
  url: string;
}

export interface ComponentVersion {
  version: number;
  /** Publisher-supplied note recorded at upload time ('' when omitted). */
  message: string;
  created_at: string;
  created_by_email: string;
  /** True for the version currently promoted into the CDN `current/` mirror. */
  is_current: boolean;
  /** Stable CDN URL prefix for the BARE-LATEST view of this component. Append
   *  a relative file path to get the always-latest copy of that file. */
  bundle_uri: string;
  files: ComponentVersionFile[];
}

export interface DeleteComponentVersionResult {
  componentId: string;
  deletedVersion: number;
  latestVersion: number | null;
  currentVersion: number | null;
  updated_at: string;
}

export interface PromoteComponentVersionResult {
  componentId: string;
  currentVersion: number;
  updated_at: string;
}

export interface ComponentDetail {
  id: string;
  organization_id: string;
  owner_user_id: string;
  owner_email: string;
  name: string;
  description: string;
  is_active: boolean;
  current_version: number | null;
  created_at: string;
  updated_at: string;
}

export interface InvitedUser {
  id: string;
  organization_id: string;
  email: string;
  role: UserRole;
  must_change_password: boolean;
  is_active: boolean;
  created_at: string;
}

export interface Organization {
  id: string;
  name: string;
}

export interface InviteUserPayload {
  email: string;
  tempPassword: string;
  /** Existing organization picked from the dropdown. Mutually exclusive with `organizationName`. */
  organizationId?: string;
  /** New organization name typed by the superadmin. The backend creates or reuses by name. */
  organizationName?: string;
}

export interface BatchInviteEntry {
  email: string;
  /** Existing organization id (UUID). Mutually exclusive with `organizationName`. */
  organizationId?: string;
  /** Org name typed by the superadmin; backend creates or reuses by name. */
  organizationName?: string;
}

export type BatchInviteStatus =
  | 'created'
  | 'conflict'
  | 'invalid'
  | 'org-not-found'
  | 'error';

export interface BatchInviteResult {
  email: string;
  status: BatchInviteStatus;
  organizationId?: string;
  organizationName?: string;
  /** Plaintext temp password — present only when status === 'created'. */
  tempPassword?: string;
  /** Human-readable explanation of a non-'created' status. */
  error?: string;
}

export const api = {
  login(email: string, password: string) {
    return request<{ user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  logout() {
    return request<{ ok: true }>('/api/auth/logout', { method: 'POST' });
  },
  me() {
    return request<{ user: AuthUser }>('/api/auth/me');
  },
  listMine() {
    return request<{ items: ComponentItem[] }>('/api/components/mine');
  },
  listTeam() {
    return request<{ items: ComponentItem[] }>('/api/components/team');
  },
  listComponentsByOrg(organizationId: string) {
    return request<{ items: ComponentItem[] }>(
      `/api/components/by-org/${encodeURIComponent(organizationId)}`,
    );
  },
  createComponent(input: { name: string; description: string }) {
    return request<{ component: ComponentItem; token: string }>('/api/components', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  deleteComponent(componentId: string) {
    return request<void>(
      `/api/components/${encodeURIComponent(componentId)}`,
      { method: 'DELETE' },
    );
  },
  deleteComponentVersion(componentId: string, version: number) {
    return request<DeleteComponentVersionResult>(
      `/api/components/${encodeURIComponent(componentId)}/versions/${encodeURIComponent(
        String(version),
      )}`,
      { method: 'DELETE' },
    );
  },
  listComponentVersions(componentId: string) {
    return request<{ component: ComponentDetail; versions: ComponentVersion[] }>(
      `/api/components/${encodeURIComponent(componentId)}/versions`,
    );
  },
  promoteComponentVersion(componentId: string, version: number) {
    return request<PromoteComponentVersionResult>(
      `/api/components/${encodeURIComponent(componentId)}/versions/${encodeURIComponent(
        String(version),
      )}/promote`,
      { method: 'POST' },
    );
  },
  rotateComponentToken(componentId: string) {
    return request<{ component: ComponentItem; token: string }>(
      `/api/components/${encodeURIComponent(componentId)}/rotate-token`,
      { method: 'POST' },
    );
  },
  inviteUser(input: InviteUserPayload) {
    return request<{ user: InvitedUser }>('/api/users', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  batchInviteUsers(entries: BatchInviteEntry[]) {
    return request<{ results: BatchInviteResult[] }>('/api/users/batch', {
      method: 'POST',
      body: JSON.stringify({ entries }),
    });
  },
  listOrganizations() {
    return request<{ items: Organization[] }>('/api/organizations');
  },
  changePassword(input: { currentPassword: string; newPassword: string }) {
    return request<{ user: AuthUser }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};
